/**
 * @typedef {import('./types.js').WaitMapItem} WaitMapItem
 */

import { ERROR_CODES } from './constants.js'

/**
 * Creates a WaitMap for managing pending RPC requests
 *
 * The WaitMap stores Promise resolve/reject callbacks for each request_id,
 * allowing async RPC calls to wait for responses. It also handles timeouts.
 *
 * Lifecycle:
 * 1. addRequest() - Store Promise callbacks when sending RPC call
 * 2. resolveRequest() - Resolve Promise when response arrives
 * 3. rejectRequest() - Reject Promise on error or timeout
 *
 * @returns {Object} WaitMap instance with methods
 */
export default () => {
  /**
   * Map of request_id to pending request info
   * @type {Object<string, WaitMapItem>}
   */
  const waitMap = {}

  /**
   * Adds a pending request to the wait map
   *
   * @param {Object} params
   * @param {string} params.request_id - Unique request ID (UUID)
   * @param {Function} params.resolve - Promise resolve callback
   * @param {Function} params.reject - Promise reject callback
   * @param {number} params.timeout - Timeout in milliseconds
   * @param {string} params.locator - Human-readable request location (for debugging)
   * @throws {Error} If request_id is empty
   */
  const addRequest = ({ request_id, resolve, reject, timeout, locator }) => {
    if (!request_id) {
      throw new Error('request_id is required')
    }

    // Store Promise callbacks
    waitMap[request_id] = {
      resolve,
      reject,
      locator,
    }

    // Set timeout to automatically reject if no response arrives
    waitMap[request_id].timeoutId = setTimeout(() => {
      rejectRequest(
        request_id,
        ERROR_CODES.TIMEOUT,
        `request timeout after ${timeout}ms`,
      )
    }, timeout)
  }

  /**
   * Resolves a pending request with successful response data
   *
   * Cleans up the wait map entry and timeout, then resolves the Promise.
   *
   * @param {string} request_id - Request ID to resolve
   * @param {*} data - Response data to return
   * @throws {Error} If request_id is empty
   */
  const resolveRequest = (request_id, data) => {
    if (!request_id) {
      throw new Error('request_id is required')
    }

    const waitItem = waitMap[request_id]

    // Request may have already timed out or been resolved
    if (!waitItem) return

    // Clean up wait map
    delete waitMap[request_id]

    // Resolve the Promise
    waitItem.resolve(data)

    // Clear timeout (no longer needed)
    clearTimeout(waitItem.timeoutId)
  }

  /**
   * Rejects a pending request with an error
   *
   * Cleans up the wait map entry and timeout, then rejects the Promise
   * with an error that includes the locator for debugging.
   *
   * @param {string} request_id - Request ID to reject
   * @param {number} error_code - Error code (e.g., 500, 408)
   * @param {string} error_message - Error message
   * @throws {Error} If request_id is empty
   */
  const rejectRequest = (request_id, error_code, error_message) => {
    if (!request_id) {
      throw new Error('request_id is required')
    }

    const waitItem = waitMap[request_id]

    // Request may have already been resolved/rejected
    if (!waitItem) return

    // Clean up wait map
    delete waitMap[request_id]

    // Create error with locator for debugging
    const err = new Error(`${waitItem.locator}: ${error_message}`)
    err.code = error_code

    // Reject the Promise
    waitItem.reject(err)

    // Clear timeout (no longer needed)
    clearTimeout(waitItem.timeoutId)
  }

  /**
   * Gets the number of pending requests
   *
   * Useful for debugging and testing.
   *
   * @returns {number} Count of pending requests
   */
  const getPendingCount = () => {
    return Object.keys(waitMap).length
  }

  /**
   * Gets all pending request IDs
   *
   * Useful for debugging and testing.
   *
   * @returns {string[]} Array of pending request IDs
   */
  const getPendingRequestIds = () => {
    return Object.keys(waitMap)
  }

  return {
    addRequest,
    resolveRequest,
    rejectRequest,
    getPendingCount,
    getPendingRequestIds,
  }
}
